<template>
  <div
    v-if="show"
    class="fixed inset-0 z-[1100] flex items-center justify-center p-2.5 sm:p-4 bg-slate-900/60 backdrop-blur-sm"
  >
    <div
      class="relative w-full max-w-lg bg-white dark:bg-[#111C30] rounded-2xl sm:rounded-3xl shadow-2xl border border-slate-200/80 dark:border-[#263449] overflow-hidden transform transition-all flex flex-col max-h-[92vh] sm:max-h-[88vh]"
    >
      <!-- Header -->
      <div
        class="px-5 sm:px-6 py-4 bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 text-white flex items-center justify-between flex-shrink-0"
      >
        <div class="flex items-center space-x-3">
          <div
            class="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-white/10 backdrop-blur-md flex items-center justify-center border border-white/20 flex-shrink-0 text-emerald-400"
          >
            <CreditCard class="w-4 h-4 sm:w-5 sm:h-5" />
          </div>
          <div>
            <h3 class="text-base sm:text-lg font-black text-white tracking-tight">Record Fee Payment</h3>
            <p class="text-[11px] sm:text-xs text-indigo-200/80 font-medium">
              Accept full or partial fee payments with instant receipt
            </p>
          </div>
        </div>
        <button
          @click="$emit('close')"
          class="p-1.5 sm:p-2 text-white/70 hover:text-white rounded-xl hover:bg-white/10 transition-colors cursor-pointer"
        >
          <X class="w-5 h-5" />
        </button>
      </div>

      <!-- Student Info Banner -->
      <div
        v-if="student"
        class="bg-indigo-50/50 dark:bg-[#18243A] border-b border-indigo-100/60 dark:border-[#263449] px-4 sm:px-6 py-2.5 flex items-center justify-between flex-shrink-0"
      >
        <div>
          <span class="text-[10px] font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-wider block"
            >Paying Student</span
          >
          <h4 class="text-xs sm:text-sm font-black text-slate-800 dark:text-[#F1F5F9]">{{ student.name }}</h4>
        </div>
        <div class="text-right">
          <span class="text-[11px] text-slate-500 dark:text-[#A6B4C8]">Roll: {{ student.rollNumber || "N/A" }}</span>
          <p class="text-[11px] font-bold text-slate-700 dark:text-[#F1F5F9]">
            {{ student.class?.name }}
            {{ student.class?.section ? `(${student.class.section})` : "" }}
          </p>
        </div>
      </div>

      <!-- Form Body with scrollable content and fixed bottom action bar -->
      <form @submit.prevent="handleSubmit" class="flex flex-col flex-1 min-h-0 overflow-hidden">
        <div class="flex-1 overflow-y-auto overscroll-contain p-4 sm:p-6 space-y-4">
          <!-- Category Selector -->
          <div>
            <label class="block text-xs font-bold text-slate-700 dark:text-[#A6B4C8] uppercase tracking-wide mb-2">
              Fee Category
            </label>
            <div class="grid grid-cols-3 gap-2">
              <button
                type="button"
                @click="setCategory('monthly')"
                :class="[
                  'p-2 sm:p-2.5 rounded-xl border-2 text-xs font-semibold transition-all flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-1.5 cursor-pointer',
                  form.feeCategory === 'monthly'
                    ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300 shadow-sm'
                    : 'border-slate-200 dark:border-[#263449] bg-white dark:bg-[#18243A] text-slate-600 dark:text-[#A6B4C8] hover:border-slate-300',
                ]"
              >
                <Calendar class="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                <span>Monthly Fee</span>
              </button>
              <button
                type="button"
                @click="setCategory('admission')"
                :class="[
                  'p-2 sm:p-2.5 rounded-xl border-2 text-xs font-semibold transition-all flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-1.5 cursor-pointer',
                  form.feeCategory === 'admission'
                    ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300 shadow-sm'
                    : 'border-slate-200 dark:border-[#263449] bg-white dark:bg-[#18243A] text-slate-600 dark:text-[#A6B4C8] hover:border-slate-300',
                ]"
              >
                <GraduationCap class="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" />
                <span>Admission Fee</span>
              </button>
              <button
                type="button"
                @click="setCategory('previous_dues')"
                :class="[
                  'p-2 sm:p-2.5 rounded-xl border-2 text-xs font-semibold transition-all flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-1.5 cursor-pointer relative',
                  form.feeCategory === 'previous_dues'
                    ? 'border-amber-500 bg-amber-50 dark:bg-amber-950/40 text-amber-900 dark:text-amber-300 shadow-sm'
                    : 'border-slate-200 dark:border-[#263449] bg-white dark:bg-[#18243A] text-slate-600 dark:text-[#A6B4C8] hover:border-slate-300',
                ]"
              >
                <History class="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
                <span>Previous Dues</span>
                <span
                  v-if="previousDuesBalance > 0"
                  class="absolute -top-1.5 -right-1 px-1.5 py-0.2 bg-amber-600 text-white rounded-full text-[9px] font-black"
                >
                  ₹{{ previousDuesBalance.toLocaleString("en-IN") }}
                </span>
              </button>
            </div>
          </div>

          <!-- If Monthly: Select Month -->
          <div v-if="form.feeCategory === 'monthly'">
            <label class="block text-xs font-semibold text-slate-700 dark:text-[#A6B4C8] mb-1">
              Select Billing Month <span class="text-red-500">*</span>
            </label>
            <select
              v-model="form.monthlyFeeRecordId"
              @change="handleMonthChange"
              required
              class="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 dark:border-[#263449] bg-white dark:bg-[#18243A] text-slate-900 dark:text-[#F1F5F9] focus:ring-2 focus:ring-emerald-500 outline-none"
            >
              <option value="" disabled>-- Select Month --</option>
              <option
                v-for="rec in payableMonthlyRecords"
                :key="rec._id"
                :value="rec._id"
              >
                {{ rec.monthName }} {{ rec.year }} — Due: ₹{{ (rec.balance || 0).toLocaleString("en-IN") }}
                {{ rec.status === "overdue" ? "(Overdue)" : "" }}
              </option>
            </select>
            <p
              v-if="payableMonthlyRecords.length === 0"
              class="text-xs text-amber-600 dark:text-amber-400 mt-1"
            >
              All generated monthly fees have been fully paid.
            </p>
          </div>

          <!-- If Previous Dues: Carried-forward details breakdown & Itemized Allocation -->
          <div v-if="form.feeCategory === 'previous_dues'" class="space-y-3">
            <div class="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/40 p-3.5 rounded-xl text-xs space-y-2.5">
              <div class="flex items-center justify-between font-bold text-amber-900 dark:text-amber-200">
                <span class="flex items-center gap-1.5">
                  <AlertCircle class="w-4 h-4 text-amber-600 flex-shrink-0" />
                  Carried-Forward Previous Class Dues
                </span>
                <span class="font-mono text-sm font-black">₹{{ previousDuesBalance.toLocaleString("en-IN") }}</span>
              </div>
              <p v-if="previousDues.message" class="text-[11px] text-amber-800 dark:text-amber-300 leading-relaxed">
                {{ previousDues.message }}
              </p>

              <!-- Allocation Mode Toggle (Requirement 4) -->
              <div class="pt-2 border-t border-amber-200/60 dark:border-amber-800/40">
                <span class="block text-[10px] font-bold uppercase tracking-wider text-amber-900 dark:text-amber-200 mb-1.5">
                  Payment Allocation Method
                </span>
                <div class="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    @click="setAllocationMode('auto')"
                    :class="[
                      'p-2 rounded-lg text-xs font-bold transition text-left border cursor-pointer',
                      allocationMode === 'auto'
                        ? 'bg-amber-600 text-white border-amber-600 shadow-2xs'
                        : 'bg-white dark:bg-[#18243A] text-slate-700 dark:text-[#A6B4C8] border-slate-200 dark:border-[#263449]'
                    ]"
                  >
                    ⚡ Auto Waterfall
                    <span class="block text-[10px] font-normal opacity-90 mt-0.5">Clears admission then oldest dues</span>
                  </button>
                  <button
                    type="button"
                    @click="setAllocationMode('manual')"
                    :class="[
                      'p-2 rounded-lg text-xs font-bold transition text-left border cursor-pointer',
                      allocationMode === 'manual'
                        ? 'bg-amber-600 text-white border-amber-600 shadow-2xs'
                        : 'bg-white dark:bg-[#18243A] text-slate-700 dark:text-[#A6B4C8] border-slate-200 dark:border-[#263449]'
                    ]"
                  >
                    🎯 Specific / Itemized
                    <span class="block text-[10px] font-normal opacity-90 mt-0.5">Allocate to specific dues</span>
                  </button>
                </div>
              </div>

              <!-- If Manual / Specific Allocation: List of unpaid obligations -->
              <div v-if="allocationMode === 'manual'" class="pt-2 space-y-2">
                <span class="block text-[10px] font-bold uppercase tracking-wider text-amber-900 dark:text-amber-200">
                  Select Item(s) & Specify Amounts
                </span>
                <div class="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                  <div
                    v-for="item in payableCarryForwardItems"
                    :key="item._id || item.description"
                    class="p-2 rounded-lg bg-white dark:bg-[#18243A] border border-amber-200/80 dark:border-amber-800/40 flex items-center justify-between gap-2"
                  >
                    <div class="min-w-0 flex-1">
                      <span class="font-bold text-[11px] text-slate-800 dark:text-[#F1F5F9] block truncate">
                        {{ item.description }}
                      </span>
                      <span class="text-[10px] text-slate-400 font-mono">
                        {{ item.originalClassName || 'Previous' }} • {{ item.originalAcademicSession || '' }} • Due: ₹{{ (item.remainingBalance || 0).toLocaleString("en-IN") }}
                      </span>
                    </div>
                    <div class="flex items-center gap-1.5 shrink-0">
                      <input
                        type="number"
                        v-model.number="item.allocatedAmount"
                        @input="syncManualAllocations"
                        min="0"
                        :max="item.remainingBalance"
                        placeholder="0"
                        class="w-20 px-2 py-1 text-xs font-mono font-bold text-right rounded border border-slate-300 dark:border-[#263449] bg-slate-50 dark:bg-[#111C30]"
                      />
                      <button
                        type="button"
                        @click="allocateItemFull(item)"
                        class="px-1.5 py-1 text-[10px] font-bold bg-amber-100 hover:bg-amber-200 text-amber-800 rounded cursor-pointer"
                        title="Fill Full Item Due"
                      >
                        Max
                      </button>
                    </div>
                  </div>
                  <div v-if="payableCarryForwardItems.length === 0" class="text-center py-2 text-slate-400 text-xs">
                    No individual itemized records found. Using Auto Waterfall mode.
                  </div>
                </div>
                <div class="flex items-center justify-between pt-1 text-[11px] font-bold text-amber-900 dark:text-amber-200">
                  <span>Total Allocated:</span>
                  <span class="font-mono">₹{{ totalManualAllocated.toLocaleString("en-IN") }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Balance Info Banner -->
          <div
            class="p-3 sm:p-3.5 rounded-xl border flex items-center justify-between gap-2"
            :class="
              currentBalance > 0
                ? 'bg-amber-50/70 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800/40 text-amber-900 dark:text-amber-200'
                : 'bg-slate-50 dark:bg-[#18243A] border-slate-200 dark:border-[#263449] text-slate-700 dark:text-[#A6B4C8]'
            "
          >
            <div>
              <span class="text-[11px] sm:text-xs font-medium text-slate-500 dark:text-[#7E8DA5] block">
                {{ form.feeCategory === 'previous_dues' ? 'Remaining Previous Class Dues' : 'Remaining Outstanding Balance' }}
              </span>
              <span class="text-base sm:text-lg font-extrabold font-mono">
                ₹{{ (currentBalance || 0).toLocaleString("en-IN") }}
              </span>
            </div>
            <button
              v-if="currentBalance > 0"
              type="button"
              @click="payFullBalance"
              class="px-2.5 py-1 text-xs font-bold text-emerald-700 dark:text-emerald-300 bg-emerald-100/80 dark:bg-emerald-950/60 hover:bg-emerald-200 rounded-lg transition flex-shrink-0 cursor-pointer"
            >
              Fill Full Balance
            </button>
          </div>

          <!-- Amount Received -->
          <div>
            <label class="block text-xs font-semibold text-slate-700 dark:text-[#A6B4C8] mb-1">
              Amount Received (₹) <span class="text-red-500">*</span>
            </label>
            <div class="relative">
              <span class="absolute inset-y-0 left-0 pl-3.5 flex items-center text-slate-400 font-bold">₹</span>
              <input
                v-model.number="form.amount"
                type="number"
                min="1"
                :max="currentBalance > 0 ? currentBalance : undefined"
                step="any"
                required
                placeholder="0.00"
                class="w-full pl-8 pr-4 py-2 text-base font-bold rounded-lg border border-slate-300 dark:border-[#263449] bg-white dark:bg-[#18243A] text-slate-900 dark:text-[#F1F5F9] focus:ring-2 focus:ring-emerald-500 outline-none transition"
              />
            </div>
            <p v-if="validationError" class="text-xs text-red-500 mt-1 font-medium">
              {{ validationError }}
            </p>
          </div>

          <!-- Payment Date & Method -->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label class="block text-xs font-semibold text-slate-700 dark:text-[#A6B4C8] mb-1">
                Payment Date
              </label>
              <input
                v-model="form.paymentDate"
                type="date"
                required
                class="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 dark:border-[#263449] bg-white dark:bg-[#18243A] text-slate-900 dark:text-[#F1F5F9] focus:ring-2 focus:ring-emerald-500 outline-none"
              />
            </div>
            <div>
              <label class="block text-xs font-semibold text-slate-700 dark:text-[#A6B4C8] mb-1">
                Payment Method <span class="text-red-500">*</span>
              </label>
              <select
                v-model="form.paymentMethod"
                required
                class="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 dark:border-[#263449] bg-white dark:bg-[#18243A] text-slate-900 dark:text-[#F1F5F9] focus:ring-2 focus:ring-emerald-500 outline-none"
              >
                <option value="Cash">Cash</option>
                <option value="UPI">UPI (GPay / PhonePe / Paytm)</option>
                <option value="Bank transfer">Bank Transfer / NEFT / IMPS</option>
                <option value="Card">Debit / Credit Card</option>
                <option value="Cheque">Cheque</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>

          <!-- Reference & Remarks -->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label class="block text-xs font-semibold text-slate-700 dark:text-[#A6B4C8] mb-1">
                Transaction / Cheque Ref
              </label>
              <input
                v-model="form.transactionReference"
                type="text"
                placeholder="e.g. UTR123456 or Chq #001"
                class="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 dark:border-[#263449] bg-white dark:bg-[#18243A] text-slate-900 dark:text-[#F1F5F9] focus:ring-2 focus:ring-emerald-500 outline-none"
              />
            </div>
            <div>
              <label class="block text-xs font-semibold text-slate-700 dark:text-[#A6B4C8] mb-1">
                Remarks (Optional)
              </label>
              <input
                v-model="form.remarks"
                type="text"
                placeholder="e.g. Paid towards Class 1 arrears"
                class="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 dark:border-[#263449] bg-white dark:bg-[#18243A] text-slate-900 dark:text-[#F1F5F9] focus:ring-2 focus:ring-emerald-500 outline-none"
              />
            </div>
          </div>
        </div>

        <!-- Buttons (flex-shrink-0 pinned at bottom) -->
        <div class="flex-shrink-0 px-4 sm:px-6 py-3.5 bg-slate-50 dark:bg-[#18243A] border-t border-slate-200 dark:border-[#263449] flex items-center justify-end space-x-3">
          <button
            type="button"
            @click="$emit('close')"
            :disabled="isSubmitting"
            class="px-4 py-2 text-sm font-semibold text-slate-600 dark:text-[#A6B4C8] hover:text-slate-800 dark:hover:text-[#F1F5F9] rounded-xl transition cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="submit"
            :disabled="isSubmitting || !canSubmit"
            class="px-5 py-2.5 text-xs sm:text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl shadow-md shadow-emerald-600/20 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            <AppLoader
              v-if="isSubmitting"
              variant="button"
              class="w-4 h-4 text-white"
            />
            <CheckCircle2 v-else class="w-4 h-4" />
            <span>{{ isSubmitting ? "Recording..." : "Record & Print Receipt" }}</span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
  import { ref, reactive, computed, watch } from "vue";
  import {
    X,
    CreditCard,
    Calendar,
    GraduationCap,
    History,
    AlertCircle,
    CheckCircle2,
  } from "lucide-vue-next";

  const props = defineProps({
    show: { type: Boolean, default: false },
    student: { type: Object, default: null },
    feeStructure: { type: Object, default: null },
    monthlyRecords: { type: Array, default: () => [] },
    previousClassDues: { type: Object, default: () => ({}) },
    carryForwardRecords: { type: Array, default: () => [] },
    preselectedCategory: { type: String, default: "monthly" },
    preselectedMonthlyRecordId: { type: String, default: "" },
    isSubmitting: { type: Boolean, default: false },
  });

  const emit = defineEmits(["close", "submit"]);

  const form = reactive({
    feeCategory: "monthly",
    monthlyFeeRecordId: "",
    amount: null,
    paymentDate: new Date().toISOString().substring(0, 10),
    paymentMethod: "Cash",
    transactionReference: "",
    remarks: "",
  });

  // Previous class dues resolution
  const previousDues = computed(() => {
    return (
      props.previousClassDues ||
      props.feeStructure?.previousClassDues ||
      props.student?.previousClassDues ||
      {}
    );
  });

  const previousDuesBalance = computed(() => {
    return (
      Number(previousDues.value?.balance) ||
      Number(props.student?.previousClassesRemaining) ||
      0
    );
  });

  // Filter only monthly records with outstanding balance
  const payableMonthlyRecords = computed(() => {
    return props.monthlyRecords.filter((rec) => rec.balance > 0);
  });

  const allocationMode = ref("auto");
  const manualItems = ref([]);

  const payableCarryForwardItems = computed(() => {
    return manualItems.value;
  });

  const totalManualAllocated = computed(() => {
    return manualItems.value.reduce((sum, itm) => sum + (Number(itm.allocatedAmount) || 0), 0);
  });

  const syncManualAllocations = () => {
    form.amount = totalManualAllocated.value;
  };

  const setAllocationMode = (mode) => {
    allocationMode.value = mode;
    if (mode === "auto") {
      form.amount = previousDuesBalance.value > 0 ? previousDuesBalance.value : null;
    } else {
      syncManualAllocations();
    }
  };

  const allocateItemFull = (item) => {
    item.allocatedAmount = item.remainingBalance;
    syncManualAllocations();
  };

  const initManualItems = () => {
    if (props.carryForwardRecords && props.carryForwardRecords.length > 0) {
      manualItems.value = props.carryForwardRecords
        .filter((r) => (r.remainingBalance || 0) > 0)
        .map((r) => ({
          ...r,
          allocatedAmount: 0,
        }));
    } else {
      const items = [];
      const prev = previousDues.value || {};
      if ((prev.unpaidAdmissionFee || 0) > 0) {
        items.push({
          feeCategory: "admission",
          description: (prev.previousClassName || "Previous Class") + " Admission Fee",
          originalClassName: prev.previousClassName || "Previous Class",
          originalAcademicSession: prev.previousSession || "",
          remainingBalance: prev.unpaidAdmissionFee,
          allocatedAmount: 0,
        });
      }
      if ((prev.unpaidMonthlyFee || 0) > 0) {
        items.push({
          feeCategory: "monthly",
          description: (prev.previousClassName || "Previous Class") + " Tuition Fee (" + (prev.unpaidMonthsCount || 0) + " months)",
          originalClassName: prev.previousClassName || "Previous Class",
          originalAcademicSession: prev.previousSession || "",
          remainingBalance: prev.unpaidMonthlyFee,
          allocatedAmount: 0,
        });
      }
      manualItems.value = items;
    }
  };

  // Calculate current outstanding balance for the selected category
  const currentBalance = computed(() => {
    if (form.feeCategory === "admission") {
      return props.feeStructure?.admissionFee?.balance || 0;
    }
    if (form.feeCategory === "previous_dues") {
      return previousDuesBalance.value;
    }
    const target = props.monthlyRecords.find(
      (r) => r._id === form.monthlyFeeRecordId
    );
    return target ? target.balance : 0;
  });

  const validationError = computed(() => {
    if (!form.amount || form.amount <= 0) {
      return "Amount must be greater than zero.";
    }
    if (form.amount > currentBalance.value) {
      return `Amount exceeds the outstanding balance of ₹${(currentBalance.value || 0).toLocaleString("en-IN")}.`;
    }
    return null;
  });

  const canSubmit = computed(() => {
    if (!form.amount || form.amount <= 0) return false;
    if (form.amount > currentBalance.value) return false;
    if (form.feeCategory === "monthly" && !form.monthlyFeeRecordId) return false;
    if (form.feeCategory === "previous_dues" && previousDuesBalance.value <= 0) return false;
    return true;
  });

  const setCategory = (cat) => {
    form.feeCategory = cat;
    if (cat === "admission") {
      form.amount = currentBalance.value > 0 ? currentBalance.value : null;
    } else if (cat === "previous_dues") {
      form.amount = previousDuesBalance.value > 0 ? previousDuesBalance.value : null;
    } else {
      if (payableMonthlyRecords.value.length > 0) {
        form.monthlyFeeRecordId = payableMonthlyRecords.value[0]._id;
        form.amount = payableMonthlyRecords.value[0].balance;
      } else {
        form.monthlyFeeRecordId = "";
        form.amount = null;
      }
    }
  };

  const handleMonthChange = () => {
    const target = props.monthlyRecords.find(
      (r) => r._id === form.monthlyFeeRecordId
    );
    if (target) {
      form.amount = target.balance;
    }
  };

  const payFullBalance = () => {
    form.amount = currentBalance.value;
  };

  // Watch for opening or preselection
  watch(
    () => props.show,
    (isShown) => {
      if (isShown) {
        form.paymentDate = new Date().toISOString().substring(0, 10);
        form.feeCategory = props.preselectedCategory || "monthly";

        if (form.feeCategory === "monthly") {
          if (props.preselectedMonthlyRecordId) {
            form.monthlyFeeRecordId = props.preselectedMonthlyRecordId;
            const target = props.monthlyRecords.find(
              (r) => r._id === props.preselectedMonthlyRecordId
            );
            form.amount = target ? target.balance : null;
          } else if (payableMonthlyRecords.value.length > 0) {
            form.monthlyFeeRecordId = payableMonthlyRecords.value[0]._id;
            form.amount = payableMonthlyRecords.value[0].balance;
          } else {
            form.monthlyFeeRecordId = "";
            form.amount = null;
          }
        } else if (form.feeCategory === "previous_dues") {
          initManualItems();
          allocationMode.value = "auto";
          form.amount = previousDuesBalance.value > 0 ? previousDuesBalance.value : null;
        } else {
          form.amount = props.feeStructure?.admissionFee?.balance || null;
        }

        form.transactionReference = "";
        form.remarks = "";
      }
    },
    { immediate: true }
  );

  const handleSubmit = () => {
    if (!canSubmit.value) return;

    // Generate unique idempotency key to protect against double click / network retry
    const idempotencyKey = `pay_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

    const payload = {
      feeCategory: form.feeCategory,
      monthlyFeeRecordId:
        form.feeCategory === "monthly" ? form.monthlyFeeRecordId : null,
      amount: form.amount,
      paymentDate: form.paymentDate,
      paymentMethod: form.paymentMethod,
      transactionReference: form.transactionReference.trim(),
      remarks: form.remarks.trim(),
      idempotencyKey,
    };

    if (form.feeCategory === "previous_dues" && allocationMode.value === "manual") {
      payload.allocations = manualItems.value
        .filter((itm) => (Number(itm.allocatedAmount) || 0) > 0)
        .map((itm) => ({
          carryForwardId: itm._id || null,
          monthlyFeeRecordId: itm.monthlyFeeRecordId || null,
          feeCategory: itm.feeCategory,
          description: itm.description,
          amount: Number(itm.allocatedAmount),
          classId: itm.originalClassId || null,
          className: itm.originalClassName || null,
          academicSession: itm.originalAcademicSession || null,
          month: itm.month || null,
          year: itm.year || null,
        }));
    }

    emit("submit", payload);
  };
</script>
